import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { motion, radius, transition } from "../../theme/tokens";
import { authTokens as t } from "../../theme/authTokens";
import AppLoader from "../../Components/Design/AppLoader";
import { useAppDispatch } from "../../Store/hooks";
import { useNotifier } from "../../Core/Notifier";
import { useLoginMutation } from "../../Api/attoDeskApi";
import { useFormik } from "formik";
import { IAuth } from "../../Api/Interface/api.interface";
import { setEnableAuth } from "../../Store/Auth/AuthSlice";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Privileges } from "../../Core/Enum/enum";
import { AuthBadge, AuthCard } from "./AuthLayout";
import { authButtonBusySx, authButtonSx, authFieldSx, authLabelSx } from "./authStyles";

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showErrorMessage } = useNotifier();
  const [login, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();

  /** Presentation-only state. None of it reaches the request. */
  const [showPassword, setShowPassword] = useState(false);
  /**
   * A mirror of the message already handed to `showErrorMessage`, kept so the
   * same text can also be shown in place, next to the fields that caused it.
   * The snackbar still fires exactly as before — this adds a surface, it does
   * not replace or reinterpret any error.
   */
  const [authError, setAuthError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    onSubmit: async (values) => {
      setAuthError(null);
      try {
        const loginResponse = await login(values).unwrap();
        if (!loginResponse.status) {
          showErrorMessage(loginResponse.message);
          setAuthError(loginResponse.message);
        } else {
          const tokenData = loginResponse.data as IAuth;
          localStorage.setItem("token", tokenData.token);
          if (tokenData.lightThemeColor) {
            localStorage.setItem("lightThemeColor", JSON.stringify(tokenData.lightThemeColor));
          }
          if (tokenData.darkThemeColor) {
            localStorage.setItem("darkThemeColor", JSON.stringify(tokenData.darkThemeColor));
          }
          dispatch(setEnableAuth());
          navigateBasedOnPrivileges(loginResponse.privileges);
          window.location.reload();
        }
      } catch (error) {
        showErrorMessage("Please check you username or password");
        setAuthError("Please check you username or password");
      }
    },
  });
  const formValid = useMemo(() => {
    return formik.values.username === "" || formik.values.password === ""
      ? false
      : true;
  }, [formik]);

  const navigateBasedOnPrivileges = (privileges: string[]) => {
    if (privileges.includes(Privileges.DashboardView)) {
      navigate("/dashboard");
    } else if (privileges.includes(Privileges.ClientView)) {
      navigate("/customer/list");
    } else if (privileges.includes(Privileges.PoliciesView)) {
      navigate("/policy-list");
    } else if (privileges.includes(Privileges.CoverageView)) {
      navigate("/policy-scenarios/list");
    } else if (privileges.includes(Privileges.PoliciesView)) {
      navigate("/policy/list");
    } else if (privileges.includes(Privileges.CoverageView)) {
      navigate("/category/list");
    } else if (privileges.includes(Privileges.InsuranceCompanyView)) {
      navigate("/brand/list");
    } else if (privileges.includes(Privileges.UserView)) {
      navigate("/user/list");
    } else if (privileges.includes(Privileges.UserRoleView)) {
      navigate("/user/role/list");
    }  else if (privileges.includes(Privileges.PrivilegeView)) {
      navigate("/user/privilege/list");
    } else if (privileges.includes(Privileges.BranchView)) {
      navigate("/branch/list");
    } else if (privileges.includes(Privileges.ThemeView)) {
      navigate("/theme-color");
    }  else if (privileges.includes(Privileges.StaffView)) {
      navigate("/team/list");
    } else if (privileges.includes(Privileges.DepartmentView)) {
      navigate("/department/list");
    } else if (privileges.includes(Privileges.PositionView)) {
      navigate("/position/list");
    } else if (privileges.includes(Privileges.ClientView)) {
      navigate("/client/meeting");
    } else if (privileges.includes(Privileges.ToDoView)) {
      navigate("/conversations/list");
    } else if (privileges.includes(Privileges.NewslettersView)) {
      navigate("/newsletters/list");
    } else if (privileges.includes(Privileges.ClientTypeView)) {
      navigate("/client_type/list");
    } else if (privileges.includes(Privileges.WorkshopView)) {
      navigate("/workshop/list");
    }
  };

  /**
   * The sign-in card.
   *
   * Everything below is presentation. The formik binding, the submit handler,
   * the error handling and `navigateBasedOnPrivileges` above are untouched, and
   * the request payload is still `{ username, password }` — the field is
   * labelled "Username" rather than "Email" because that is genuinely what
   * `POST /login` authenticates on.
   */
  return (
    <AuthCard>
      <Box component="form" onSubmit={formik.handleSubmit} noValidate>
        <AuthBadge />

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 22, sm: 24.5 },
            fontWeight: 700,
            letterSpacing: "-0.022em",
            color: t.text,
          }}
        >
          Welcome to GT Brokers
        </Typography>
        <Typography sx={{ mt: 0.75, mb: 3, fontSize: 14, lineHeight: 1.6, color: t.muted }}>
          Sign in to manage your business.
        </Typography>

        {/* ------------------------------------------------ error state */}
        {authError && (
          <Box
            role="alert"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              mb: 2.5,
              px: 1.75,
              py: 1.5,
              borderRadius: `${radius.md}px`,
              border: `1px solid ${alpha("#E11D48", 0.26)}`,
              backgroundColor: alpha("#E11D48", 0.06),
            }}
          >
            <ErrorOutlineRoundedIcon
              aria-hidden
              sx={{ fontSize: 19, color: "#E11D48", mt: "1px", flexShrink: 0 }}
            />
            <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: "#BE123C" }}>
              {authError}
            </Typography>
          </Box>
        )}

        {/* ---------------------------------------------------- fields */}
        <Box sx={{ mb: 2.25 }}>
          <Typography component="label" htmlFor="username" sx={authLabelSx}>
            Username
          </Typography>
          <TextField
            fullWidth
            id="username"
            placeholder="Enter your username"
            autoComplete="username"
            autoFocus
            error={Boolean(authError)}
            sx={authFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineRoundedIcon
                    aria-hidden
                    sx={{ fontSize: 20, color: alpha(t.muted, 0.85) }}
                  />
                </InputAdornment>
              ),
            }}
            {...formik.getFieldProps("username")}
            onChange={(event) => {
              if (authError) setAuthError(null);
              formik.handleChange(event);
            }}
          />
        </Box>

        <Box>
          <Typography component="label" htmlFor="password" sx={authLabelSx}>
            Password
          </Typography>
          <TextField
            fullWidth
            id="password"
            placeholder="Enter your password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            error={Boolean(authError)}
            sx={authFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon
                    aria-hidden
                    sx={{ fontSize: 19, color: alpha(t.muted, 0.85) }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    sx={{
                      color: alpha(t.muted, 0.85),
                      "&:hover": { color: t.blue },
                      transition: transition(["color"], motion.fast),
                    }}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...formik.getFieldProps("password")}
            onChange={(event) => {
              if (authError) setAuthError(null);
              formik.handleChange(event);
            }}
          />
        </Box>

        {/* -------------------------------------------- forgot password
            Rendered unconditionally, at every breakpoint — no viewport
            check, no overflow trap. A plain router link to the page below.
            No "remember me": no such behaviour exists in this app, and a
            checkbox that does nothing is worse than no checkbox. */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <Link
            component={RouterLink}
            to="/forgot-password"
            underline="none"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: t.blue,
              borderRadius: `${radius.xs}px`,
              p: 0.25,
              transition: transition(["color"], motion.fast),
              "&:hover": { color: t.blueDeep, textDecoration: "underline" },
              "&:focus-visible": {
                outline: `2px solid ${alpha(t.blue, 0.5)}`,
                outlineOffset: 2,
              },
            }}
          >
            Forgot password?
          </Link>
        </Box>

        {/* --------------------------------------------- primary CTA */}
        <Button
          variant="contained"
          fullWidth
          disableElevation
          disabled={!formValid || isLoading}
          type="submit"
          sx={[authButtonSx, { mt: 2.5 }, ...(isLoading ? [authButtonBusySx] : [])]}
        >
          {isLoading ? (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.25 }}>
              {/* `inherit` draws the ring in the button's own text colour. */}
              <AppLoader variant="inline" size={16} inherit />
              <span>Signing in...</span>
            </Box>
          ) : (
            "Sign In"
          )}
        </Button>
      </Box>
    </AuthCard>
  );
};

export default React.memo(Login);
